import React from 'react';

/** Returns true if the URL uses a safe scheme (http, https, or mailto). */
function isSafeUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        return ['http:', 'https:', 'mailto:'].includes(parsed.protocol);
    } catch {
        // Relative URLs are safe (no protocol = no javascript:)
        return !url.trimStart().toLowerCase().startsWith('javascript:');
    }
}

/**
 * Converts markdown links [text](url) to React anchor elements.
 * Only renders links with safe URL schemes (http, https, mailto).
 * Other markdown syntax is passed through as plain text.
 */
export function renderMarkdownLinks(markdown: string, linkClassName?: string): (string | React.ReactElement)[] {
    const parts: (string | React.ReactElement)[] = [];
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = linkRegex.exec(markdown)) !== null) {
        if (match.index > lastIndex) {
            parts.push(markdown.slice(lastIndex, match.index));
        }
        const href = match[2];
        if (isSafeUrl(href)) {
            parts.push(
                React.createElement(
                    'a',
                    {
                        key: match.index,
                        href,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        className: linkClassName,
                    },
                    match[1],
                ),
            );
        } else {
            // Unsafe URL — render as plain text
            parts.push(match[1]);
        }
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < markdown.length) {
        parts.push(markdown.slice(lastIndex));
    }

    return parts;
}

/**
 * Converts a hex color to rgba with specified opacity for tinted backgrounds.
 * Handles both 3-character (#FFF) and 6-character (#FFFFFF) hex formats.
 */
export function hexToTintedRgba(color: string | undefined, opacity = 0.08): string | undefined {
    if (!color) return undefined;
    if (color.startsWith('#')) {
        let hex = color.slice(1);
        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
        if (hex.length !== 6) return undefined;
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) return undefined;
        const clampedOpacity = Math.max(0, Math.min(1, opacity));
        return `rgba(${r}, ${g}, ${b}, ${clampedOpacity.toString()})`;
    }
    return color;
}
